import React, { useMemo } from 'react';
import clsx from 'clsx';
import { Box, Typography, Button, styled } from '@mui/material';

import { ArrowForward, ReportProblemOutlined } from '@mui/icons-material';

import AlertSlider from 'components/shared/AlertSlider';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import { dateUtilities, numberUtilities } from 'utils';
import { useStore } from 'hooks';
import { useTranslation } from 'react-i18next';

const AlertBox = ({ onViewSensorDataClick, condition = null }) => {
  const { t } = useTranslation();
  const { deviceHistoryStore, notificationsStore } = useStore();

  const { _closed, happened_from, happened_to, latest_trigger_at, message, gateway } = condition;

  const { min, max } = notificationsStore.getMinMax(condition?._alarms_operand_id);
  const countedCriteriaValue = numberUtilities.celsiusWrapper(condition?.context?.temp_c, {
    signsAmount: 1,
  });

  const fetchSensorData = () => {
    if (!gateway?.id) return;
    deviceHistoryStore.setSelectedGatewayId(gateway.id);
    const { from, to } = dateUtilities.getDatesByPeriod('custom', {
      fromDate: happened_from || latest_trigger_at,
      toDate: latest_trigger_at || happened_from,
    });
    deviceHistoryStore.setPeriod({
      from,
      to,
    });
    onViewSensorDataClick();
  };

  const caption = useMemo(() => {
    const caption = message?.alert_caption || condition.alarm?.alert_caption || '';

    if (['Low Temperature', 'High Temperature'].includes(caption)) {
      return t(`shared.AlertBox.${caption}`);
    }

    return t(`shared.AlertBox.alert`);
  }, [message, condition, t]);

  return (
    <StyledRootBox>
      <Box
        className={clsx('alert_wrapper', {
          active: !_closed,
          resolved: _closed,
        })}
      >
        <Box className="wrapper_main_box">
          <Box className="icon_wrapper">
            {!_closed ? (
              <ReportProblemIcon className="alert_icon active" />
            ) : (
              <ReportProblemOutlined className="alert_icon resolved" />
            )}
          </Box>

          <Box className="main_box">
            <Box className="info_box">
              <Box className="title_box desktop_only">
                <Typography variant="title desktop_only">{caption}</Typography>
                <Typography
                  className={clsx('status', {
                    active: !_closed,
                    resolved: _closed,
                  })}
                >
                  {_closed ? t('shared.AlertBox.resolved') : t('shared.AlertBox.active')}
                </Typography>
              </Box>
              <Typography variant="body2" className="desktop_only">
                {gateway?._gateway_types_name ? gateway?._gateway_types_name + ' - ' : ' '}
                {gateway?.id ? `SN ${gateway?.id}` : 'Unknown Gateway'}
              </Typography>
              <Typography variant="body2">
                {t('shared.AlertBox.issueStarted')} -{' '}
                <span className="white_text">
                  {dateUtilities.formatDateIncludingToday(happened_from)}
                </span>
              </Typography>
              <Typography variant="body2">
                {_closed ? t('shared.AlertBox.resolvedOn') : t('shared.AlertBox.latestTrigger')}
                {' - '}
                <span className="white_text">
                  {dateUtilities.formatDateIncludingToday(
                    _closed ? happened_to : latest_trigger_at,
                  )}
                </span>
              </Typography>
            </Box>
            <Box className="slider_box">
              <AlertSlider value={countedCriteriaValue} min={min} max={max} />
            </Box>

            <Box className="mobile_only">
              <Typography variant="title">{gateway?._coolers_name}</Typography>
              <Typography variant="body2">{gateway?.name}</Typography>
            </Box>
          </Box>
        </Box>
        <Box className="action_box">
          <Button className="view_button" endIcon={<ArrowForward />} onClick={fetchSensorData}>
            {t('shared.AlertBox.viewSensorData')}
          </Button>
        </Box>
      </Box>
    </StyledRootBox>
  );
};

export default AlertBox;

const StyledRootBox = styled(Box)(({ theme: { palette, breakpoints } }) => ({
  borderRadius: '16px',
  [breakpoints.up('sm')]: {
    background: palette.elevation.overlay3,
  },
  '.icon_wrapper': {
    [breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  '.alert_wrapper': {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '401px',
    height: '272px',
    borderRadius: '16px',
    padding: '24px',
    justifyContent: 'space-between',
    '&.active': {
      border: `1px solid ${palette.status.red}`,
    },
    [breakpoints.down('sm')]: {
      all: 'unset',
      '&.active': {
        border: 'none',
      },
    },
  },

  [breakpoints.down('sm')]: {
    border: 'none',
    padding: '16px',
  },

  '.alert_icon': {
    color: palette.whiteEmphasis.high,
    width: '24px',
    height: '24px',
    marginRight: '14px',
    marginTop: '4px',
    '&.active': {
      color: palette.status.red,
    },
    '&.resolved': {
      color: palette.whiteEmphasis.medium,
    },
    [breakpoints.down('sm')]: {
      width: '48px',
      height: '48px',
    },
  },

  '.wrapper_main_box': {
    display: 'flex',
  },

  '.main_box': {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    width: '100%',

    [breakpoints.down('sm')]: {
      gap: '12px',
      alignItems: 'center',
    },

    '.info_box': {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',

      [breakpoints.down('sm')]: {
        alignItems: 'center',
      },
    },

    '.slider_box': {
      width: '100%',

      [breakpoints.down('sm')]: {
        marginTop: '16px',
      },
    },
  },

  '.status': {
    borderRadius: '36px',
    fontSize: '12px',
    padding: '2px 4px',

    '&.active': {
      color: palette.status.red,
      border: `1px solid ${palette.status.red}`,
    },

    '&.resolved': {
      color: palette.status.green,
      border: `1px solid ${palette.status.green}`,
    },

    [breakpoints.down('sm')]: {
      display: 'none',
    },
  },

  '.action_box': {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    marginTop: '12px',
  },

  '.view_button': {
    borderRadius: '4px',
    textTransform: 'none',
    display: 'flex',
    margin: '0 auto',
    padding: '12px 14px 12px 24px',
    color: '#ffffff99',
    border: `1px solid ${palette.primary.main}`,
    svg: {
      fontSize: '17px',
      marginBottom: '2px',
    },
  },

  '.white_text': {
    color: palette.whiteEmphasis.high,
  },

  '.desktop_only': {
    [breakpoints.down('sm')]: {
      display: 'none',
    },
  },

  '.mobile_only': {
    [breakpoints.up('sm')]: {
      display: 'none',
    },
  },
  '.title_box': {
    display: 'flex',
    justifyContent: 'space-between',
  },
}));
